import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Building, Clock } from "lucide-react";
import { CalendarioAdmin } from '@/services/reservaService';
import reservaService from '@/services/reservaService';

interface AdminTimeSlotsProps {
  calendarioData: CalendarioAdmin | null;
  selectedDate: string;
  loading?: boolean;
}

/**
 * Componente para exibir os blocos de horários para administradores
 * Mostra todas as salas e seus respectivos horários com status das reservas
 */
const AdminTimeSlots: React.FC<AdminTimeSlotsProps> = ({
  calendarioData,
  selectedDate,
  loading = false
}) => {

  /**
   * Obter cor do badge baseado no status da reserva
   */
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'confirmado':
        return 'bg-green-100 text-green-800';
      case 'reservado':
        return 'bg-blue-100 text-blue-800';
      case 'pendente':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelado':
        return 'bg-red-100 text-red-800';
      case 'concluido':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  /**
   * Formatar data para exibição
   */
  const formatDisplayDate = (dateString: string) => {
    const [year, month, day] = dateString.split('-');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
      .toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardHeader>
              <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[1, 2, 3, 4].map((j) => (
                  <div key={j} className="h-16 bg-gray-100 rounded animate-pulse"></div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!calendarioData || calendarioData.salas.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-gray-500">
          <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>Nenhum horário disponível para esta data.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Cards por sala */}
      {calendarioData.salas.map((sala) => (
        <Card key={sala.id}>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Building className="w-5 h-5 text-blue-600" />
              {sala.nome}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sala.blocos.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                <p className="text-sm">Nenhum bloco de horário configurado para esta sala.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {sala.blocos.map((bloco) => (
                  <div 
                    key={bloco.id} 
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-medium text-sm text-gray-900">
                          {bloco.hora_inicio} - {bloco.hora_fim}
                        </span>
                        <span className="text-sm font-semibold text-blue-600">
                          R$ {bloco.valor.toFixed(2)}
                        </span>
                      </div>
                      
                      {bloco.reserva ? (
                        <div className="mt-2">
                          <p className="text-sm text-gray-900 font-medium">
                            {bloco.reserva.cliente}
                          </p>
                          <Badge className={`text-xs mt-1 ${getStatusColor(bloco.reserva.status)}`}>
                            {reservaService.formatStatus(bloco.reserva.status)}
                          </Badge>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 mt-1">
                          Horário disponível
                        </p>
                      )}
                    </div>
                    
                    {/* Indicador visual de status */}
                    <div className={`w-4 h-4 rounded-full flex-shrink-0 ${
                      bloco.reserva 
                        ? bloco.reserva.status.toLowerCase() === 'confirmado' 
                          ? 'bg-green-500' 
                          : bloco.reserva.status.toLowerCase() === 'reservado'
                          ? 'bg-blue-500'
                          : bloco.reserva.status.toLowerCase() === 'pendente'
                          ? 'bg-yellow-500'
                          : bloco.reserva.status.toLowerCase() === 'cancelado'
                          ? 'bg-red-500'
                          : bloco.reserva.status.toLowerCase() === 'concluido'
                          ? 'bg-purple-500'
                          : 'bg-gray-500'
                        : 'bg-gray-300'
                    }`} />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default AdminTimeSlots;